import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search as SearchIcon, X, TrendingUp, SlidersHorizontal, ChevronDown } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import TopHeader from '../components/layout/TopHeader.jsx'
import { ProductCardRow, ProductCard, RowSkeleton, ProductCardSkeleton } from '../components/ui/index.jsx'
import { productService } from '../services/api.js'

const TRENDING = ['Whisky', 'Wine', 'Vodka', 'Hennessy', 'Rum', 'Beer', 'Gin', 'Tequila']
const SORT_OPTIONS = [
  { label: 'Relevance',       value: 'relevance' },
  { label: 'Price: Low–High', value: 'price_asc' },
  { label: 'Price: High–Low', value: 'price_desc' },
  { label: 'Name A–Z',        value: 'name_asc' },
  { label: 'Name Z–A',        value: 'name_desc' },
  { label: 'Newest First',    value: 'newest' },
]
const BROWSE_CATS = [
  { name: 'Whisky', icon: '🥃', color: '#8B4513' },
  { name: 'Wine',   icon: '🍷', color: '#722F37' },
  { name: 'Vodka',  icon: '🍸', color: '#4169E1' },
  { name: 'Beer',   icon: '🍺', color: '#DAA520' },
  { name: 'Gin',    icon: '🌿', color: '#228B22' },
  { name: 'Rum',    icon: '🏝️', color: '#8B6914' },
]

function norm(p) {
  return {
    id: p.id,
    name: p.description || p.stock_code,
    price: p.inclprice ?? p.inclprice2 ?? 0,
    image: p.image || null,
    primary_image: p.primary_image || null,
    stock_code: p.stock_code,
    category: p.category_name || p.category || '',
    brand: p.brand || '',
    inStock: true,
  }
}

function useDebounce(value, delay = 320) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export default function SearchPage() {
  const navigate = useNavigate()
  const inputRef = useRef(null)
  const [query, setQuery]               = useState('')
  const [sort, setSort]                 = useState('relevance')
  const [viewMode, setViewMode]         = useState('list')
  const [showSort, setShowSort]         = useState(false)
  const [activeBrand, setActiveBrand]   = useState('')

  const debouncedQuery = useDebounce(query, 320)

  // ── Real API search ─────────────────────────────────────────
  const { data: searchData, isLoading: isSearchLoading } = useQuery({
    queryKey: ['products', 'search', debouncedQuery],
    queryFn: () => productService.getAll({ search: debouncedQuery, limit: 200 }),
    enabled: debouncedQuery.trim().length > 0,
    staleTime: 60_000,
  })

  // ── Brands for pills ────────────────────────────────────────
  const { data: brandsData } = useQuery({
    queryKey: ['products', 'brands'],
    queryFn: () => productService.getBrands(),
    staleTime: 10 * 60 * 1000,
  })
  const brands = Array.isArray(brandsData) ? brandsData : []

  const raw = Array.isArray(searchData)
    ? searchData
    : (searchData?.items ?? searchData?.products ?? [])
  const allResults = raw.map(norm)

  const results = useMemo(() => {
    let list = [...allResults]
    if (activeBrand) list = list.filter(p => p.brand === activeBrand)
    switch (sort) {
      case 'price_asc':  return [...list].sort((a, b) => a.price - b.price)
      case 'price_desc': return [...list].sort((a, b) => b.price - a.price)
      case 'name_asc':   return [...list].sort((a, b) => a.name.localeCompare(b.name))
      case 'name_desc':  return [...list].sort((a, b) => b.name.localeCompare(a.name))
      case 'newest':     return [...list].sort((a, b) => (b.id || 0) - (a.id || 0))
      default:           return list
    }
  }, [allResults, sort, activeBrand])

  const isTyping    = query !== debouncedQuery
  const hasQuery    = debouncedQuery.trim().length > 0 || activeBrand

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="Search" />

      {/* ── Sticky search bar ── */}
      <div className="px-4 pt-2 pb-3 sticky top-14 z-30 bg-surface-dark/95 backdrop-blur-md border-b border-surface-border/50">
        <div className="relative">
          <SearchIcon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            ref={inputRef}
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search products, brands, categories…"
            className="input-field pl-10 pr-10"
          />
          {query && (
            <button
              onClick={() => { setQuery(''); setActiveBrand(''); inputRef.current?.focus() }}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 active:text-white transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {hasQuery && (
          <div className="flex items-center justify-between mt-2.5">
            <p className="text-xs text-gray-500 font-display">
              {isTyping || isSearchLoading ? (
                <span className="text-gray-600">Searching…</span>
              ) : (
                <span>
                  <span className="text-white font-semibold">{results.length}</span>
                  {' '}result{results.length !== 1 ? 's' : ''}
                  {debouncedQuery ? ` for "${debouncedQuery}"` : ''}
                  {activeBrand ? <span className="text-primary"> · {activeBrand}</span> : ''}
                </span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <div className="flex bg-surface-elevated rounded-lg p-0.5 border border-surface-border">
                <button onClick={() => setViewMode('list')}
                  className={`px-2 py-1 rounded-md text-xs font-display transition-colors ${viewMode === 'list' ? 'bg-primary text-white' : 'text-gray-400'}`}>
                  List
                </button>
                <button onClick={() => setViewMode('grid')}
                  className={`px-2 py-1 rounded-md text-xs font-display transition-colors ${viewMode === 'grid' ? 'bg-primary text-white' : 'text-gray-400'}`}>
                  Grid
                </button>
              </div>
              <div className="relative">
                <button onClick={() => setShowSort(s => !s)}
                  className="flex items-center gap-1.5 bg-surface-elevated border border-surface-border rounded-lg px-3 py-1.5 text-xs font-display text-gray-300">
                  <SlidersHorizontal size={12} />
                  {SORT_OPTIONS.find(o => o.value === sort)?.label}
                  <ChevronDown size={11} className={`transition-transform ${showSort ? 'rotate-180' : ''}`} />
                </button>
                {showSort && (
                  <div className="absolute right-0 top-full mt-1 bg-surface-card border border-surface-border rounded-xl shadow-xl z-50 min-w-[160px] overflow-hidden">
                    {SORT_OPTIONS.map(opt => (
                      <button key={opt.value}
                        onClick={() => { setSort(opt.value); setShowSort(false) }}
                        className={`w-full text-left px-4 py-2.5 text-sm font-display transition-colors ${sort === opt.value ? 'bg-primary/20 text-primary' : 'text-gray-300 hover:bg-surface-elevated'}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Discovery (no query yet) ── */}
      {!hasQuery ? (
        <div className="px-4 pt-4 space-y-6">
          {brands.length > 0 && (
            <div>
              <p className="text-gray-500 text-xs font-display font-semibold uppercase tracking-wider mb-3">Filter by Brand</p>
              <div className="flex gap-2 overflow-x-auto pb-1 scroll-hide -mx-1 px-1">
                {activeBrand && (
                  <button onClick={() => setActiveBrand('')}
                    className="flex items-center gap-1 bg-primary/20 border border-primary/50 rounded-full px-3.5 py-1.5 text-xs font-display text-primary whitespace-nowrap flex-shrink-0">
                    <X size={11} /> All
                  </button>
                )}
                {brands.slice(0, 20).map(b => (
                  <button key={b} onClick={() => setActiveBrand(prev => prev === b ? '' : b)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-display whitespace-nowrap flex-shrink-0 border transition-colors ${
                      activeBrand === b
                        ? 'bg-primary text-white border-primary'
                        : 'bg-surface-card border-surface-border text-gray-300 active:bg-primary/20'
                    }`}>
                    {b}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-gray-500 text-xs font-display font-semibold uppercase tracking-wider mb-3">Trending Searches</p>
            <div className="flex flex-wrap gap-2">
              {TRENDING.map(t => (
                <button key={t} onClick={() => setQuery(t)}
                  className="flex items-center gap-1.5 bg-surface-card border border-surface-border rounded-full px-3.5 py-2 text-sm font-display text-gray-300 active:bg-primary/20 active:border-primary/40 transition-colors">
                  <TrendingUp size={12} className="text-primary" />{t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-gray-500 text-xs font-display font-semibold uppercase tracking-wider mb-3">Browse by Category</p>
            <div className="grid grid-cols-3 gap-2">
              {BROWSE_CATS.map(cat => (
                <button key={cat.name} onClick={() => setQuery(cat.name)}
                  className="flex flex-col items-center gap-1.5 bg-surface-card border border-surface-border rounded-2xl py-4 active:scale-95 transition-transform"
                  style={{ borderColor: `${cat.color}33` }}>
                  <span className="text-2xl">{cat.icon}</span>
                  <span className="text-xs font-display text-gray-300">{cat.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

      ) : (
        /* ── Results ── */
        <div className={`pb-4 ${viewMode === 'grid' ? 'px-4 pt-3' : 'px-4 pt-2 space-y-2'}`}>
          {isTyping || isSearchLoading ? (
            viewMode === 'grid'
              ? <div className="grid grid-cols-2 gap-3">{Array(4).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)}</div>
              : Array(4).fill(0).map((_, i) => <RowSkeleton key={i} />)
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <span className="text-5xl mb-4">🔍</span>
              <p className="text-white font-heading text-lg mb-1">Nothing found</p>
              <p className="text-gray-400 font-display text-sm">No products match your search</p>
              <button onClick={() => { setQuery(''); setActiveBrand('') }}
                className="mt-5 btn-outline py-2 px-6 text-sm">Clear search</button>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 gap-3">
              {results.map(p => <ProductCard key={p.id} product={p} onClick={() => navigate(`/product/${p.id}`)} />)}
            </div>
          ) : (
            results.map(p => <ProductCardRow key={p.id} product={p} onClick={() => navigate(`/product/${p.id}`)} />)
          )}
        </div>
      )}
    </div>
  )
}
